import type { EnrollmentItem } from '@shared/flow/enrollment';

/**
 * A queue item's display identity for row labels, play-card headers, and
 * ARIA-labels (Fix 4, MAJOR bd finding — Warm Intro plan U9, R4): ~55% of
 * warm-intro targets are LinkedIn-sourced with no email. Interpolating a
 * null `contact_email` straight into a template literal renders the LITERAL
 * STRING "null" (e.g. `aria-label="Remove null"`), which is exactly what a
 * screen reader announces and exactly what `ab snapshot`'s accessibility
 * tree reads. Falls back to the contact's LinkedIn URL (`contact_linkedin`,
 * joined in at read time), then the raw contact id, so this NEVER renders
 * "null" — some real identifier for the contact always comes out.
 *
 * U1 (Gong Flow Play + Queue redesign) extends the chain with `contact_name`
 * (the mirror's `salesforce_contact.name`, also joined in at read time) as
 * the TOP priority — a real display name reads better than an email address
 * everywhere this identity shows up (row label, Remove/Edit aria-labels,
 * PlayPicker's assign-to prefix). Existing null-name callers are unaffected:
 * the chain still ends at contact_id.
 *
 * ONE helper, used everywhere a queue item needs a human-readable identity
 * (QueueBody row display + every aria-label, EnrollmentItemEditor's
 * aria-labels) — originally duplicated ad hoc as
 * `item.contact_email` in each component; this is the single source of
 * truth so a future null-identity source doesn't need the same fix three
 * times.
 */
export function contactDisplayIdentity(item: Pick<EnrollmentItem, 'contact_name' | 'contact_email' | 'contact_linkedin' | 'contact_id'>): string {
  return item.contact_name ?? item.contact_email ?? item.contact_linkedin ?? item.contact_id;
}
